/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import {
	Button,
	Drawer,
	DrawerCloseButton,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerOverlay,
	Flex,
	Image,
	Menu,
	MenuButton,
	MenuDivider,
	MenuItem,
	MenuList,
	Text,
	Tooltip,
	useDisclosure,
	useToast,
	IconButton,
	useBreakpointValue,
} from '@chakra-ui/react';
import Logo from '../assets/images/logo-border-white-92px.png';
import { Link, useLocation } from 'react-router-dom';
import { faChevronDown, faBell, faUnlockAlt, faCalendarWeek, faMagic, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChangeLanguage from '../components/ChangeLanguage';
import c from '../constant';
import useStore from '../store';
import { capitalizeFirstLetter } from '../utils';
import { isUserPermitted } from '../utils/checker';
import './AiMagicButton.css';
import { useAiPanel } from './AiPanel/AiPanelContext';
import NotificationList from './NotificationList';
import { useDebounceEffect } from './form/useDebounceEffect';

const QUERY_CURRENT_USER = gql`
	query {
		getCurrentuser {
			id
			username
			fullName
			fullNameKhmer
			role
			position
			avatar
			camDigiKeyId
			canAssignUserToCompanyProposal
			canUpdateCompanyLicensing
			canUpdateCompanyGovernance
			canUpdateCompanyContact
			canUploadDoneFileToCompanyProposal
			canViewListReportToCompanyProposal
			canAutoreassignProposal
			canAssignUserToCompanyReport
			canApproveRejectLeaveRequest
			canApproveMissionRecord
			canExportMissionRecordData
			canViewLeaveRequestReport
			canNotApplyForLeave
			canApproveRejectDeactivateAgent
			canFinalApproveRejectAgent
			canUploadDoneFileToComplaint
			canAssignUserToComplaint
			canViewComplaintReport
			announcingItem
			paymentAgentRegistration
			paymentPublicService
			paymentLicense
			canUpdateLicense
			paymentFine
			canUpdateFine
			canCreateIssuedLetter
			canUpdateIssuedLetter
			canDeleteIssuedLetter
			canSubmitTrainingCourse
			canExportTrainingCourse
			companyReportType
			homeMenu
			department
			office
			mdd
			ews
			nprp
			isTwoFactorEnabled
		}
	}
`;

const GET_EMPLOYEE_ID_BY_USER_ID = gql`
	query get($id: String!) {
		getEmployeeIdByUserId(id: $id)
	}
`;

const Q_COUNT = gql`
	query {
		getCountUnreadNotifications
	}
`;

const MUTATION_MARK_ALL_AS_READ = gql`
	mutation {
		markAllNotificationAsRead
	}
`;

const QUERY_CURRENT_USER_EXAM_CANDIDATE_STATUS = gql`
	query {
		getCurrentUserExamCandidateStatus {
			examCandidates
			examCandidateAlert
			dateOfLastRank
			monthsSinceLastRank
		}
	}
`;

function Header({ hidden = false }) {
	const location = useLocation();
	const toast = useToast();
	const { t } = useTranslation();
	const { isOpen: isAiPanelOpen, togglePanel, closePanel, isAvailable: isAiPanelAvailable } = useAiPanel();
	const [loadEmployeeId, { data: employeeId }] = useLazyQuery(GET_EMPLOYEE_ID_BY_USER_ID);
	const [loadCurrentUser, { data }] = useLazyQuery(QUERY_CURRENT_USER);
	const [loadCountUnreadNotifications, { loading: loadingCount, data: dataCount }] = useLazyQuery(Q_COUNT);
	const [markAllAsRead, { error: errorMarkAll, loading: loadingMarkAll, data: dataMarkAll }] = useMutation(MUTATION_MARK_ALL_AS_READ);
	const setCurrentUser = useStore((state) => state.setCurrentUser);
	const setCountUnreadNotifications = useStore((state) => state.setCountUnreadNotifications);
	const { currentUser } = useStore((state) => state.currentUser);
	const { currentLang } = useStore((state) => state.currentLang);
	const { countUnreadNotifications } = useStore((state) => state.countUnreadNotifications);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [closeAlert, setCloseAlert] = useState(false);
	const [loadExamCandidateStatus, { data: dataExamCandidateStatus, loading: loadingExamCandidateStatus, error: errorExamCandidateStatus }] =
		useLazyQuery(QUERY_CURRENT_USER_EXAM_CANDIDATE_STATUS);

	const examCandidateStatus = dataExamCandidateStatus?.getCurrentUserExamCandidateStatus;
	const showExamAlert = examCandidateStatus?.examCandidateAlert === true && !closeAlert;

	const showSmallScreen = useBreakpointValue({
		base: true,
		sm: true,
		md: false,
		lg: false, // >= 992px
		xl: false, // >= 1280px
	});

	useDebounceEffect(loadCountUnreadNotifications, 200, [location]);

	useEffect(() => {
		loadCurrentUser();
		loadCountUnreadNotifications();
		loadExamCandidateStatus();
		const handleActive = () => {
			if (document?.hidden) return;
			loadCountUnreadNotifications();
		};

		document.addEventListener('visibilitychange', handleActive);

		return () => {
			document.removeEventListener('visibilitychange', handleActive);
		};
	}, []);

	useEffect(() => {
		loadCurrentUser();
		checkMaintenanceStatus();

		const intervalId = setInterval(() => {
			checkMaintenanceStatus();
		}, 60000);
		return () => clearInterval(intervalId);
	}, []);

	useEffect(() => {
		if (data) {
			setCurrentUser(data?.getCurrentuser);
			loadEmployeeId({
				variables: { id: data?.getCurrentuser?.id },
			});
		}
	}, [data]);

	useEffect(() => {
		if (dataMarkAll?.markAllNotificationAsRead) {
			loadCountUnreadNotifications();
		}
	}, [dataMarkAll]);

	useEffect(() => {
		if (errorMarkAll) {
			toast({
				title: t('Mark all as read error'),
				status: 'error',
				isClosable: true,
				position: 'top-right',
			});
		}
	}, [errorMarkAll]);

	useEffect(() => {
		if (dataCount) {
			setCountUnreadNotifications(parseInt(dataCount?.getCountUnreadNotifications ?? 0));
		}
	}, [dataCount]);

	function getDisplayName(user) {
		let displayName = capitalizeFirstLetter(user?.username);
		if (currentLang === 'kh') {
			displayName = user?.fullNameKhmer ? user?.fullNameKhmer : user?.fullName ? user?.fullName : capitalizeFirstLetter(user?.username);
		} else {
			displayName = user?.fullName ? user?.fullName : user?.fullNameKhmer ? user?.fullNameKhmer : capitalizeFirstLetter(user?.username);
		}
		return displayName;
	}

	// Who may open the AI panel. Deliberately narrow: every AI action bills real
	// tokens to the office account, so it is admins plus nprp users explicitly
	// granted `nprpAiSpellChecker`. The `assistant` role alone is not enough.
	const canUseAi = useMemo(() => {
		const roles = (currentUser?.role || '').split(',');
		if (roles?.includes('admin')) return true;
		return roles?.includes('nprp') && isUserPermitted(['nprpAiSpellChecker'], [...(currentUser?.nprp ?? [])]);
	}, [currentUser]);

	// The Note of Presentation detail page is the only screen that supplies AI
	// context today, so the toggle is hidden everywhere else rather than opening
	// an empty panel. Matched on pathname only — the id lives in the query
	// string, which does not affect which page this is.
	const isAiEnabledPage = useMemo(() => location?.pathname === '/note-of-presentation/detail', [location?.pathname]);

	// Navigating away hides the toggle, so an open panel would otherwise be
	// stranded with no way to close it.
	useEffect(() => {
		if (isAiPanelOpen && !(isAiEnabledPage && canUseAi)) {
			closePanel();
		}
	}, [isAiEnabledPage, canUseAi, isAiPanelOpen, closePanel]);

	const [status, setStatus] = useState(null);
	function checkMaintenanceStatus() {
		axios.get(c.baseFilePath + `status/status.json?timestamp=${new Date().getTime()}`).then(function (response) {
			setStatus(response?.data?.status);
		});
	}

	return (
		<>
			<Flex display={hidden === true ? 'none' : 'block'} className="hidden-print" bg="#232F3E" justifyContent="center" boxShadow="0px 3px 3px #00000029">
				<Flex
					// boxShadow="0px 3px 3px #00000029"
					// bg="primary.dark"
					bg="#232F3E"
					h="40px"
					maxW="95%"
					p="0.5px"
					flex="1"
					alignItems="center"
					justifyContent="space-between"
				>
					<Link to="/">
						<Flex alignItems="center">
							<Image src={Logo} h="25px" w="auto" ml="16px" />
							<Text
								className="hide-mobile"
								mt={currentLang === 'kh' ? '1px' : '4px'}
								fontFamily={currentLang === 'kh' ? 'Moul' : 'Rajdhani'}
								fontWeight={currentLang !== 'kh' ? '600' : '400'}
								fontSize="14px"
								ml="1"
								color="white"
							>
								{t('INSURANCE REGULATOR of CAMBODIA')}
							</Text>
							<Text fontFamily={'Moul'} fontSize="1px" ml="1" pointerEvents="none">
								.
							</Text>
							<Text fontFamily={'Hanuman'} fontSize="1px" ml="1" pointerEvents="none">
								.
							</Text>
						</Flex>
					</Link>
					<Flex>
						{isAiPanelAvailable && canUseAi && isAiEnabledPage && (
							<Tooltip label={t('AI Assistant')}>
								<Flex onClick={togglePanel} cursor="pointer" h="40px" w="56px" justifyContent="center" alignItems="center">
									<div className={isAiPanelOpen ? 'ai-magic-btn is-active' : 'ai-magic-btn'}>
										<span className="ai-magic-btn__ring" />
										<FontAwesomeIcon icon={faMagic} style={{ fontSize: 16, color: isAiPanelOpen ? '#48BB78' : 'white' }} />
									</div>
								</Flex>
							</Tooltip>
						)}
						{currentUser?.isTwoFactorEnabled === false && (
							<Tooltip
								className="hidden-print"
								isOpen={c.env === 'production' ? true : false}
								label={
									currentLang === 'kh'
										? 'មិនបានបើកមុខងារ 2FA។ សូមបើកវាដើម្បីការពារគណនីរបស់អ្នក។'
										: '2FA is not enabled. Please turn it on to protect your account.'
								}
							>
								<Link to={`/totp`}>
									<Flex
										cursor="pointer"
										_hover={{ bg: 'rgba(236, 240, 241,.2)' }}
										h="40px"
										w="40px"
										justifyContent="center"
										alignItems="center"
										pl="8px"
										pr="8px"
									>
										<FontAwesomeIcon icon={faUnlockAlt} style={{ fontSize: 18, color: 'rgba(241, 196, 15,1.0)' }} />
									</Flex>
								</Link>
							</Tooltip>
						)}
						{employeeId?.getEmployeeIdByUserId && (
							<Tooltip label={t('Time Sheet')}>
								<Link to={`/timesheet?id=${employeeId?.getEmployeeIdByUserId}`}>
									<Flex
										cursor="pointer"
										_hover={{ bg: 'rgba(236, 240, 241,.2)' }}
										h="40px"
										w="40px"
										justifyContent="center"
										alignItems="center"
										pl="8px"
										pr="8px"
									>
										<FontAwesomeIcon icon={faCalendarWeek} style={{ fontSize: 18, color: 'white' }} />
									</Flex>
								</Link>
							</Tooltip>
						)}
						<Flex
							onClick={onOpen}
							w="60px"
							h="40px"
							justifyContent="center"
							alignItems="center"
							cursor="pointer"
							_hover={{ bg: 'rgba(236, 240, 241,.2)' }}
						>
							<FontAwesomeIcon icon={faBell} style={{ fontSize: 16, color: countUnreadNotifications > 0 ? '#e74c3c' : 'white', marginTop: 4 }} />
							{countUnreadNotifications > 0 && (
								<Text userSelect="none" color="#e74c3c" mt="-12px" fontWeight="bold" fontSize="18px">
									{countUnreadNotifications}
								</Text>
							)}
						</Flex>
						<Flex _hover={{ bg: 'rgba(236, 240, 241,.2)' }} h="40px" justifyContent="center" alignItems="center" pl="8px" mr="8px">
							<ChangeLanguage />
						</Flex>
						<Menu>
							<MenuButton cursor="pointer">
								<Flex>
									<Text fontWeight="500" color="#d5dbdb">
										{currentUser?.username ? getDisplayName(currentUser) : ''}{' '}
									</Text>
									<FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 16, color: '#d5dbdb', marginTop: 2, marginLeft: 6 }} />
								</Flex>
							</MenuButton>
							<MenuList zIndex={999} pt="4" pb="4" pl="3" pr="3" borderRadius={c.borderRadius}>
								<Link to="/account">
									<MenuItem>{t('Account')}</MenuItem>
								</Link>
								<MenuDivider />
								{employeeId?.getEmployeeIdByUserId && (
									<>
										<Link to={`/employee/detail?id=${employeeId?.getEmployeeIdByUserId}`}>
											<MenuItem>{t('Curriculum Vitae')}</MenuItem>
										</Link>
										<MenuDivider />
									</>
								)}

								{currentUser?.role === 'admin' && (
									<>
										<Link to="/users?page=1">
											<MenuItem color="#3498db">{t('Users')}</MenuItem>
										</Link>
										<Link to="/settings">
											<MenuItem color="#3498db">{t('Settings')}</MenuItem>
										</Link>

										<MenuDivider />
									</>
								)}

								<MenuItem
									onClick={() => {
										sessionStorage.clear();
										window.location.reload();
									}}
								>
									{t('Sign Out')}
								</MenuItem>
							</MenuList>
						</Menu>
					</Flex>
				</Flex>
				{status === 0 && (
					<Flex flex="1" bg="#F1F2F5" h="40px" alignItems="center" justifyContent="center">
						<Text color="#2980b9" fontWeight={600} fontSize={17}>
							🛠 Server is under maintenance.
						</Text>
					</Flex>
				)}

				{showExamAlert && (
					<Flex bg="#67a353" color="white" align="center" h={showSmallScreen ? '50px' : '35px'}>
						<Text flex="1" textAlign="center" fontWeight="600" fontSize="md" color="white">
							{currentLang === 'kh'
								? 'សេចក្តីជូនដំណឹង៖ ការប្រឡងឡើងថ្នាក់រយៈពេល ២ឆ្នាំ របស់លោក/លោកស្រី នឹងប្រព្រឹត្តទៅក្នុងពេលឆាប់ៗខាងមុខនេះ។ សូមត្រៀមខ្លួន សម្រាប់ការប្រឡង។'
								: 'Notice: This is to inform you that your two-year promotion examination is coming soon. Please be prepared.'}
						</Text>

						<IconButton
							aria-label="Close"
							onClick={() => setCloseAlert(true)}
							icon={<FontAwesomeIcon icon={faTimes} />}
							size="sm"
							variant="ghost"
							color="white"
							_hover={{
								bg: 'whiteAlpha.300',
							}}
							mr={2}
						/>
					</Flex>
				)}
			</Flex>
			<Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
				<DrawerOverlay />
				<DrawerContent>
					<DrawerCloseButton />
					<DrawerHeader>{t('Notifications')}</DrawerHeader>
					<Flex overflow="scroll">{false === loadingCount && <NotificationList onClose={onClose} />}</Flex>
					<DrawerFooter p="2" bg="#ecf0f1">
						<Button isLoading={loadingMarkAll} size="sm" variant="solid" bg="white" onClick={markAllAsRead}>
							{t('Mark all as read')}
						</Button>
					</DrawerFooter>
				</DrawerContent>
			</Drawer>
		</>
	);
}

export default Header;
